import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const DEFAULT_CURRENCY = { code: 'USD', symbol: '$', locale: 'en-US' };

const SUPPORTED = {
  USD: { symbol: '$', locale: 'en-US', name: 'US Dollar' },
  GBP: { symbol: '£', locale: 'en-GB', name: 'British Pound' },
  EUR: { symbol: '€', locale: 'de-DE', name: 'Euro' },
  CAD: { symbol: 'CA$', locale: 'en-CA', name: 'Canadian Dollar' },
  AUD: { symbol: 'A$', locale: 'en-AU', name: 'Australian Dollar' },
  INR: { symbol: '₹', locale: 'en-IN', name: 'Indian Rupee' },
  SGD: { symbol: 'S$', locale: 'en-SG', name: 'Singapore Dollar' },
  AED: { symbol: 'AED', locale: 'ar-AE', name: 'UAE Dirham' },
};

export const SUPPORTED_CURRENCY_LIST = Object.entries(SUPPORTED).map(([code, meta]) => ({
  code,
  ...meta,
}));

const CurrencyContext = createContext(null);

export function detectCurrencyFromBrowser() {
  const langs =
    typeof navigator !== 'undefined'
      ? navigator.languages?.length
        ? navigator.languages
        : [navigator.language || 'en-US']
      : ['en-US'];
  const loc = (langs[0] || 'en-US').toLowerCase();
  if (loc.startsWith('en-gb')) return 'GBP';
  if (loc.startsWith('en-in') || loc.startsWith('hi')) return 'INR';
  if (loc.startsWith('en-ca')) return 'CAD';
  if (loc.startsWith('en-au')) return 'AUD';
  if (loc.startsWith('en-sg')) return 'SGD';
  if (loc.startsWith('ar-ae') || loc.startsWith('ar')) return 'AED';
  if (loc.startsWith('de') || loc.startsWith('fr') || loc.startsWith('es')) return 'EUR';
  return 'USD';
}

export const CurrencyProvider = ({ children, isAuthenticated }) => {
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [loading, setLoading] = useState(Boolean(isAuthenticated));

  const loadCurrency = useCallback(async () => {
    if (!isAuthenticated) {
      setCurrency(DEFAULT_CURRENCY);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get('/api/user/currency');
      const data = res.data?.currency || res.data;
      if (data?.code) {
        setCurrency({
          code: data.code,
          symbol: data.symbol || SUPPORTED[data.code]?.symbol || '$',
          locale: data.locale || SUPPORTED[data.code]?.locale || 'en-US',
        });
      }
    } catch {
      setCurrency(DEFAULT_CURRENCY);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadCurrency();
  }, [loadCurrency]);

  const formatAmount = useCallback(
    (amount, options = {}) => {
      const n = Number(amount);
      if (!Number.isFinite(n)) return `${currency.symbol}0`;
      const { maximumFractionDigits = 0, minimumFractionDigits = 0 } = options;
      try {
        return new Intl.NumberFormat(currency.locale, {
          style: 'currency',
          currency: currency.code,
          maximumFractionDigits,
          minimumFractionDigits,
        }).format(n);
      } catch {
        return `${currency.symbol}${n.toLocaleString('en-US', { maximumFractionDigits })}`;
      }
    },
    [currency]
  );

  const setCurrencyCode = useCallback(async (code) => {
    const res = await axios.put('/api/user/currency', { code });
    const data = res.data?.currency || res.data;
    if (data?.code) {
      setCurrency({
        code: data.code,
        symbol: data.symbol,
        locale: data.locale,
      });
    }
    return data;
  }, []);

  const value = useMemo(
    () => ({
      currency,
      loading,
      formatAmount,
      setCurrency: setCurrencyCode,
      refreshCurrency: loadCurrency,
      supportedCurrencies: SUPPORTED_CURRENCY_LIST,
    }),
    [currency, loading, formatAmount, setCurrencyCode, loadCurrency]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return ctx;
}

export function useCurrencyOptional() {
  return useContext(CurrencyContext);
}
